// src/pages/CollectionPage.js

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import SongListItem from '../components/SongListItem';
import { getAllSongs } from '../utils/songUtils';
import { tagIcons } from '../constants/tagConfig';
import { collectionDescriptions } from '../constants/collectionConfig';
import SearchBar from '../components/SearchBar';

// Styled Components
const PageContainer = styled.div`
  padding: 40px 80px 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #E3E3E3; /* Pale Stone Gray */
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 40px 40px 120px;
  }

  @media (max-width: 480px) {
    padding: 30px 20px 80px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100% - 40px);
  max-width: 1200px;
  margin-top: 20px;
  margin-bottom: 0;

  @media (max-width: 480px) {
    width: calc(100% - 20px);
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  justify-content: flex-start;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

const SearchBarContainer = styled.div`
  width: 100%;
  margin-top: -30px;
  margin-bottom: 10px;
`;

const CategoryTitle = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin: 0;
  font-size: 36px;
  line-height: 1.2;

  @media (max-width: 480px) {
    text-align: center;
    font-size: 28px;
  }
`;

const CategoryIcon = styled.div`
  font-size: 32px;
  display: flex;
  align-items: center;
  color: #2E2E2E;

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const SongList = styled.div`
  width: calc(100% - 40px);
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: calc(100% - 20px);
  }
`;

const EmptyMessage = styled.p`
  font-family: 'Rubik', sans-serif;
  color: #6A7D8B; /* Muted Slate Blue */
  font-size: 18px;
  margin-top: 30px;
  text-align: center;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const Description = styled.div`
  font-family: 'Rubik', sans-serif;
  color: #2E2E2E;
  font-size: 16px;
  line-height: 1.6;
  text-align: left;
  width: 100%;
  margin: 0;
  padding: 0;

  p {
    margin: 0;
  }

  a {
    color: #2E2E2E;
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.2s ease;
    
    &:hover {
      color: #000;
      text-decoration: underline;
    }

    &:visited {
      color: #4A4A4A;
    }
  }

  @media (max-width: 480px) {
    font-size: 14px;
    text-align: center;
  }
`;

const normalizeUrl = (url) => {
  if (!url) return url;
  return url.replace(/^www\./, '');
};

// Convert prompter name to URL-friendly category name
const prompterToCategory = (prompter) => {
  if (!prompter) return '';
  return prompter
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .trim(); // Remove leading/trailing spaces
};

// Convert category name back to display format
const categoryToDisplay = (category) => {
  if (!category) return '';
  return category
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function CollectionPage() {
  const { category } = useParams();
  const [allSongs, setAllSongs] = useState([]);
  const [visibleSongs, setVisibleSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingSongId, setPlayingSongId] = useState(null);
  const [songProgress, setSongProgress] = useState({});
  const observer = useRef();

  const songsPerLoad = 8;

  // Check if this is a prompter category
  const isPrompterCategory = category.startsWith('by-');
  const categoryKey = isPrompterCategory 
    ? categoryToDisplay(category.replace('by-', ''))
    : category.charAt(0).toUpperCase() + category.slice(1);
  
  // Get display name from collection config if available, otherwise use the category key
  const displayCategory = collectionDescriptions[categoryKey]?.displayName || categoryKey;
  const IconComponent = tagIcons[categoryKey];

  useEffect(() => {
    const fetchSongs = async () => {
      try {
        setLoading(true);
        setError(null);
        const all = await getAllSongs();
        
        const filteredSongs = all.filter((song) => {
          // Ensure song URLs are normalized when comparing
          if (song.song_file_wm) {
            song.song_file_wm = normalizeUrl(song.song_file_wm);
          }
          if (song.song_art) {
            song.song_art = normalizeUrl(song.song_art);
          }

          if (isPrompterCategory) {
            // For prompter categories, match against the prompter name
            const prompterCategory = prompterToCategory(song.song_prompter);
            return prompterCategory === category.replace('by-', '');
          } else {
            // For regular categories, match against tags (case-insensitive)
            return song.song_tags.some(tag => 
              tag.toLowerCase() === displayCategory.toLowerCase()
            );
          }
        });
        
        // Sort songs: featured first, then by date
        const sortedSongs = filteredSongs.sort((a, b) => {
          if (a.song_featured && !b.song_featured) return -1;
          if (!a.song_featured && b.song_featured) return 1;
          return new Date(b.date_added) - new Date(a.date_added);
        });
        
        setAllSongs(sortedSongs);
        setFilteredSongs(sortedSongs);
        setVisibleSongs(sortedSongs.slice(0, songsPerLoad));
      } catch (err) {
        console.error('Error fetching songs:', err);
        setError('Failed to fetch songs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSongs();
  }, [category, displayCategory, isPrompterCategory]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowercaseQuery = query.toLowerCase();
    
    const newFilteredSongs = allSongs.filter(song => 
      song.song_title.toLowerCase().includes(lowercaseQuery) ||
      song.song_artist.toLowerCase().includes(lowercaseQuery) ||
      song.song_tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
    );
    
    setFilteredSongs(newFilteredSongs);
    setVisibleSongs(newFilteredSongs.slice(0, songsPerLoad));
  };

  const loadMoreSongs = useCallback(() => {
    setVisibleSongs((prevSongs) => {
      const nextSongs = filteredSongs.slice(
        prevSongs.length,
        prevSongs.length + songsPerLoad
      );
      return [...prevSongs, ...nextSongs];
    });
  }, [filteredSongs]);

  const sentinelRef = useRef();

  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        visibleSongs.length < filteredSongs.length
      ) {
        loadMoreSongs();
      }
    });

    if (sentinelRef.current) {
      observer.current.observe(sentinelRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loading, visibleSongs, filteredSongs, loadMoreSongs]);

  const handlePlayToggle = (songId, progress) => {
    if (typeof progress === 'number') {
      // Handle seeking
      setSongProgress(prev => ({
        ...prev,
        [songId]: progress
      }));
    } else {
      // Handle play/pause
      setPlayingSongId(prevId => prevId === songId ? null : songId);
    }
  };

  // Get the description for the current category
  const getDescription = () => {
    // Always use categoryKey for looking up collection data
    return collectionDescriptions[categoryKey]?.description;
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{`${displayCategory} Collection on Tempo Stock`}</title>
        <meta
          name="description"
          content={
            getDescription() || 'Explore this collection on Tempo Stock.'
          }
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tempo Stock" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta
          property="og:title"
          content={`${displayCategory} Collection on Tempo Stock`}
        />
        <meta
          property="og:description"
          content={getDescription() || 'Explore this collection on Tempo Stock.'}
        />
        {/* No og:image for category pages as requested */}
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta
          name="twitter:title"
          content={`${displayCategory} Collection on Tempo Stock`}
        />
        <meta
          name="twitter:description"
          content={getDescription() || 'Explore this collection on Tempo Stock.'}
        />
        {/* No twitter:image for category pages as requested */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
      </Helmet>

      <HeaderContainer>
        <TitleContainer>
          {IconComponent && (
            <CategoryIcon>
              <IconComponent />
            </CategoryIcon>
          )}
          <CategoryTitle>{displayCategory}</CategoryTitle>
        </TitleContainer>
        {getDescription() && (
          <Description>
            <ReactMarkdown>{getDescription()}</ReactMarkdown>
          </Description>
        )}
        <SearchBarContainer>
          <SearchBar 
            onSearch={handleSearch} 
            placeholder="Search this collection..."
          />
        </SearchBarContainer>
      </HeaderContainer>

      {loading && <EmptyMessage>Loading songs...</EmptyMessage>}
      {error && <EmptyMessage>{error}</EmptyMessage>}

      {!loading && !error && (
        <>
          {filteredSongs.length > 0 ? (
            <>
              <SongList>
                {visibleSongs.map((song) => (
                  <SongListItem
                    key={song.song_id}
                    song={song}
                    isPlaying={playingSongId === song.song_id}
                    onPlayToggle={(progress) => handlePlayToggle(song.song_id, progress)}
                    progress={songProgress[song.song_id] || 0}
                    isTagCategory={!isPrompterCategory}
                    currentCategory={isPrompterCategory ? '' : category}
                  />
                ))}
              </SongList>
              {visibleSongs.length < filteredSongs.length && (
                <>
                  <Sentinel ref={sentinelRef} />
                  <EmptyMessage>Loading more songs...</EmptyMessage>
                </>
              )}
            </>
          ) : (
            <EmptyMessage>
              {searchQuery ? 'No songs found matching your search.' : 'No songs found in this category.'}
            </EmptyMessage>
          )}
        </>
      )}
    </PageContainer>
  );
}

export default CollectionPage; 